import { MENTION_ITEM_TYPES, type Member, type MentionItemType } from '@hive/shared';
import type { Message } from '@/data/comms';

// What Comms shows, worked out from messages and the composer's text. Pure, so
// it's unit-tested without Supabase.

/** Messages from one author within this many minutes run together under one name. */
const RUN_MINUTES = 5;

export interface MessageRun {
  authorKey: string;
  messages: Message[];
}

export interface DayGroup {
  /** yyyy-mm-dd, local time. */
  day: string;
  runs: MessageRun[];
}

function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Who a message is from, for grouping: the member, or an imported name. */
export function authorKey(m: Pick<Message, 'author_id' | 'author_kind' | 'import_meta'>): string {
  if (m.author_id) return `${m.author_kind}:${m.author_id}`;
  const meta = (m.import_meta ?? {}) as { user_name?: string; user_id?: string };
  return `import:${meta.user_id ?? meta.user_name ?? 'unknown'}`;
}

/** Messages (oldest first) grouped by local day, then by runs from one author. */
export function groupMessages(messages: readonly Message[]): DayGroup[] {
  const days: DayGroup[] = [];
  for (const m of messages) {
    const day = localDay(m.created_at);
    let group = days.at(-1);
    if (!group || group.day !== day) {
      group = { day, runs: [] };
      days.push(group);
    }
    const run = group.runs.at(-1);
    const prev = run?.messages.at(-1);
    const key = authorKey(m);
    if (
      run &&
      prev &&
      run.authorKey === key &&
      Date.parse(m.created_at) - Date.parse(prev.created_at) <= RUN_MINUTES * 60_000
    ) {
      run.messages.push(m);
    } else {
      group.runs.push({ authorKey: key, messages: [m] });
    }
  }
  return days;
}

/** "Today", "Yesterday", or "Monday 20 October". */
export function dayLabel(day: string, today: string): string {
  if (day === today) return 'Today';
  const y = new Date(`${today}T00:00:00`);
  y.setDate(y.getDate() - 1);
  if (day === localDay(y.toISOString())) return 'Yesterday';
  return new Date(`${day}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(day.slice(0, 4) !== today.slice(0, 4) ? { year: 'numeric' } : {}),
  });
}

/** An @ or # being typed just before the caret, for autocomplete. */
export interface MentionQuery {
  sigil: '@' | '#';
  query: string;
  /** Where the sigil is, so it can be replaced. */
  start: number;
}

export function mentionQuery(text: string, caret: number): MentionQuery | null {
  const before = text.slice(0, caret);
  const m = /(^|[\s(])([@#])([^\s@#[\]()]{0,40})$/.exec(before);
  if (!m) return null;
  return { sigil: m[2] as '@' | '#', query: m[3]!, start: caret - m[3]!.length - 1 };
}

/** The text with the typed @query or #query swapped for a token (and a space). */
export function insertMention(
  text: string,
  q: MentionQuery,
  caret: number,
  token: string,
): { text: string; caret: number } {
  const next = `${text.slice(0, q.start)}${token} ${text.slice(caret)}`;
  return { text: next, caret: q.start + token.length + 1 };
}

/** The first http(s) link in a body, for its preview. */
export function firstUrl(body: string): string | null {
  const m = /https?:\/\/[^\s<>()\]]+[^\s<>()\].,;:!?'"]/.exec(body);
  return m ? m[0] : null;
}

/** Turns mention tokens into markdown links with a mention: URL, so ReactMarkdown can render them as chips. */
export function mentionsToLinks(body: string): string {
  return body.replace(
    new RegExp(`([@#])\\[([^\\]]*)\\]\\((member|${MENTION_ITEM_TYPES.join('|')}):([0-9a-fA-F-]{36})\\)`, 'g'),
    (_w, sigil: string, label: string, type: string, id: string) => `[${sigil}${label}](mention:${type}/${id})`,
  );
}

export function parseMentionHref(href: string | undefined): { type: 'member' | MentionItemType; id: string } | null {
  const m = href ? /^mention:([a-z_]+)\/([0-9a-fA-F-]{36})$/.exec(href) : null;
  return m ? { type: m[1] as 'member' | MentionItemType, id: m[2]! } : null;
}

export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '🙏', '🔥', '✅'];

/** Who wrote a message, as shown: a member, "Claude", or an imported name. */
export function authorName(m: Pick<Message, 'author_id' | 'import_meta'>, members: readonly Member[]): string {
  const member = members.find((x) => x.id === m.author_id);
  if (member) return member.display_name;
  const meta = (m.import_meta ?? {}) as { user_name?: string; bot?: boolean };
  if (meta.user_name) return `${meta.user_name} (${meta.bot ? 'bot' : 'Slack'})`;
  return 'Someone';
}
